import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Persisted layout mode shared by the widget and the layouts.
export type ChatWidgetLayout = 'floating' | 'sidebar' | 'fullscreen';

const DEFAULT_LAYOUT: ChatWidgetLayout = 'floating';

const isLayout = (value: unknown): value is ChatWidgetLayout =>
  value === 'floating' || value === 'sidebar' || value === 'fullscreen';

type ChatWidgetLayoutState = {
  mode: ChatWidgetLayout;
  setMode: (next: ChatWidgetLayout) => void;
  restoreMode: ChatWidgetLayout | null;
  setRestoreMode: (next: ChatWidgetLayout | null) => void;
};

const useStore = create<ChatWidgetLayoutState>()(
  persist(
    set => ({
      mode: DEFAULT_LAYOUT,
      setMode: next => set({ mode: next }),
      restoreMode: null,
      setRestoreMode: next => set({ restoreMode: next }),
    }),
    {
      name: 'percy.chat-widget-layout',
      partialize: state => ({ mode: state.mode }),
      // Coerce an unknown persisted value back to the default rather than trusting it.
      merge: (persisted, current) => {
        const mode = isLayout((persisted as Partial<ChatWidgetLayoutState>)?.mode)
          ? (persisted as ChatWidgetLayoutState).mode
          : DEFAULT_LAYOUT;
        return { ...current, mode };
      },
    },
  ),
);

const useChatWidgetLayout = () => {
  const { mode, setMode, restoreMode, setRestoreMode } = useStore();
  return {
    mode,
    setMode,
    restoreMode,
    setRestoreMode,
    isSidebar: mode === 'sidebar',
    isFullscreen: mode === 'fullscreen',
  };
};

export default useChatWidgetLayout;
